import React from "react";
import {
  Search,
  ListIcon,
  Grid3x3,
  MapIcon,
  TrendingDown,
  TrendingUp,
  CalendarDays,
  Type,
  SlidersHorizontal,
  ChevronDown,
} from "lucide-react";
import { Input } from "../shadcn/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../shadcn/ui/select";
import LoadingSpinner from "./LoadingSpinner";
import Pagination from "./Pagination";
import PageHeader from "./PageHeader";

/**
 * TablePageLayout - Reusable table page layout component
 *
 * Provides consistent layout for table-based pages with optional:
 * - Search bar
 * - Filter dropdowns
 * - Order by dropdown
 * - Pagination
 * - Right-side action buttons
 *
 * @param {Object} props
 * @param {string} props.title - Page title (required)
 * @param {ReactNode} props.rightActions - Optional action buttons (e.g., "Add New User")
 * @param {boolean} props.showSearch - Show search bar (default: false)
 * @param {string} props.searchValue - Search input value
 * @param {Function} props.onSearchChange - Search input change handler
 * @param {Function} props.onSearchSubmit - Search form submit handler
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {Array} props.filters - Array of filter configurations
 * @param {boolean} props.showOrderBy - Show order by dropdown (default: false)
 * @param {string} props.orderBy - Current order by value
 * @param {Function} props.onOrderByChange - Order by change handler
 * @param {Array} props.orderByOptions - Order by options
 * @param {boolean} props.loading - Loading state
 * @param {string} props.emptyMessage - Message when no data
 * @param {ReactNode} props.children - Table content
 * @param {boolean} props.showPagination - Show pagination (default: false)
 * @param {number} props.currentPage - Current page number
 * @param {number} props.totalCount - Total items count
 * @param {number} props.pageSize - Items per page
 * @param {Function} props.onPageChange - Page change handler
 */
const TablePageLayout = ({
  // Required
  title,

  // Optional - Header Info
  subtitle,
  breadcrumbs,

  // Optional - Right actions
  rightActions = null,

  // Optional - Search
  showSearch = false,
  searchValue = "",
  onSearchChange = () => {},
  onSearchSubmit = () => {},
  searchPlaceholder = "Search...",

  // Optional - Filters
  filters = [],

  // Optional - Order By
  showOrderBy = false,
  orderBy = "",
  onOrderByChange = () => {},
  orderByOptions = [],

  // Content
  loading = false,
  emptyMessage = "No data found",
  children,

  // Optional - Pagination
  showPagination = false,
  currentPage = 1,
  totalCount = 0,
  pageSize = 10,
  onPageChange = () => {},
  // Optional - View Mode (controlled toggle). If viewMode is NOT passed, static decorative toggle shows as before.
  viewMode = null,
  onViewModeChange = null,
  viewOptions = null, // e.g. ['list','grid'] or ['list','grid','map']. null = show all 3 static.
  bottomContent = null,
}) => {
  return (
    <div className="flex flex-col min-h-full w-full">
      <PageHeader
        title={title}
        totalCount={totalCount > 0 ? totalCount : undefined}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        rightContent={
          <>
            {rightActions && <div>{rightActions}</div>}

            {/* Filter Dropdowns (dynamic based on filters array) */}
            {filters.map((filter, index) => (
              <React.Fragment key={filter.id || index}>
                <label htmlFor={filter.id} className="text-sm font-medium text-gray-700 whitespace-nowrap sr-only">
                  {filter.label}
                </label>
                <Select value={filter.value} onValueChange={filter.onChange}>
                  <SelectTrigger className="w-[180px] h-9 bg-white" id={filter.id}>
                    <SelectValue placeholder={filter.placeholder || "Select..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {filter.options.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </React.Fragment>
            ))}

            {/* View Toggle - controlled if viewMode prop is passed, static otherwise */}
            <div className="inline-flex bg-gray-200 rounded-md">
              {/* List button - always shown unless viewOptions excludes it */}
              {(!viewOptions || viewOptions.includes("list")) && (
                <button
                  className={`flex items-center justify-center p-2 transition-all duration-200 ${
                    viewMode === "list" || viewMode === null
                      ? "bg-black text-white shadow-sm"
                      : "text-black hover:bg-gray-300"
                  }`}
                  title="List"
                  onClick={() => onViewModeChange && onViewModeChange("list")}
                >
                  <ListIcon className="w-4 h-4" />
                </button>
              )}
              {/* Grid button - always shown unless viewOptions excludes it */}
              {(!viewOptions || viewOptions.includes("grid")) && (
                <button
                  className={`flex items-center justify-center p-2 transition-all duration-200 ${
                    viewMode === "grid" ? "bg-black text-white shadow-sm" : "text-black hover:bg-gray-300"
                  }`}
                  title="Grid"
                  onClick={() => onViewModeChange && onViewModeChange("grid")}
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
              )}
              {/* Map button - shown only if viewOptions includes 'map' OR if no viewOptions (legacy static behavior) */}
              {(!viewOptions || viewOptions.includes("map")) && (
                <button
                  className={`flex items-center justify-center p-2 transition-all duration-200 ${
                    viewMode === "map" ? "bg-black text-white shadow-sm" : "text-black hover:bg-gray-300"
                  }`}
                  title="Map"
                  onClick={() => onViewModeChange && onViewModeChange("map")}
                >
                  <MapIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Default Sort Dropdown styling parity */}
            {/* <div className="relative">
              <button className="flex items-center justify-between w-40 px-3 py-2 text-sm bg-white border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black">
                <span className="flex items-center text-gray-700">Sort by</span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            </div> */}

            {/* Default Filter button styling parity */}
            {/* <button className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input h-9 px-3 bg-white hover:bg-gray-50">
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
            </button> */}
          </>
        }
        bottomContent={
          <>
            {bottomContent}
            {/* Search Bar (optional) */}
            {showSearch && (
              <div className="pt-4 pb-3 w-full">
                <form onSubmit={onSearchSubmit} className="relative">
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={onSearchChange}
                    className="pr-10 bg-white"
                  />
                </form>
              </div>
            )}
          </>
        }
      />

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 w-full flex-1">
        {/* Main Content - Table */}
        <div className="flex justify-center w-full">
          <div className="w-full">
            {loading ? (
              <LoadingSpinner message="Loading..." />
            ) : !children ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground">{emptyMessage}</p>
              </div>
            ) : (
              children
            )}
          </div>
        </div>

        {/* Pagination (optional) */}
        {showPagination && totalCount > 0 && (
          <div className="flex justify-center mt-6">
            <Pagination
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={pageSize}
              siblingCount={1}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TablePageLayout;
